// Death shader nodes for the instanced enemy families (ENEMY-DEATHS, owner 2026-11-02). Only compiled in when the new
// deaths are on (?deaths=new or the ?deathlab preview); live foes (anim2.w = 0, anim4.x = 0) render exactly as before.
// Per instance:
//   anim2.w = dissolve progress u (0 = none)                  — DISINTEGRATE (Singularity collapse, high-rank Nova Lance)
//   anim3   = (r, g, b, angle): energy colour + a model-space angle (toward the blast / needle direction / cut tilt axis)
//   anim4   = (mode, a, b, heat): mode 1 = blown-apart piece (a = part bit mask; handled in the vertex stage, kit.ts)
//                                 mode 2 = hole (a = height, b = radius)            — Finger Beam
//                                 mode 3 / 4 = cut, keep below / above (a = height, b = tilt) — Scythe Disc
// Hole and cut faces: the body material is double-sided under this flag, so back faces seen through the opening read as a
// solid cross-section in the energy colour that cools to dark. Render-only.
import { abs, attribute, clamp, color, cos, dot, float, floor, fract, frontFacing, max, mix, positionGeometry, select, sin, smoothstep, step, uniform, vec3 } from 'three/tsl';

/** a11y (Reduce flashing): scales the white-hot disintegration flash; view.ts sets it from FLASH each frame */
export const deathFlashK = uniform(1);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

/** progress at which the dissolve flash is done and the break-up starts */
const FLASH_END = 0.16;

export function deathNodes(): { mask: N; emissive: N; outlineColor: (ink: number) => N } {
  const A2 = attribute('anim2', 'vec4') as N, A3 = attribute('anim3', 'vec4') as N, A4 = attribute('anim4', 'vec4') as N;
  const G = positionGeometry as N, mode: N = A4.x, heat: N = A4.w, col: N = A3.xyz;
  const is = (m: number): N => step(abs(mode.sub(m)), 0.5);

  // ---- DISINTEGRATE: flake cells, blast side first, white-hot flash then the blast colour
  const u: N = A2.w;
  const cell: N = floor(G.mul(9));
  const h: N = fract((sin(dot(cell, vec3(12.9898, 78.233, 37.719)) as N) as N).mul(43758.5453));
  const proj: N = G.x.mul(sin(A3.w)).add(G.z.mul(cos(A3.w)));
  const side: N = clamp(float(0.5).sub(proj.mul(0.55)), 0, 1);
  const rim: N = clamp(float(1).sub(G.x.mul(G.x).add(G.z.mul(G.z)).sqrt().mul(0.6)), 0, 1);
  const f: N = h.mul(0.36).add(side.mul(0.54)).add(rim.mul(0.1));
  const th: N = u.sub(FLASH_END).div(1 - FLASH_END).mul(1.12).sub(0.06); // < 0 while u = 0: nothing cut
  const keepDis: N = step(th, f);
  const edge: N = float(1).sub(smoothstep(th, th.add(0.11), f)).mul(smoothstep(FLASH_END * 0.6, FLASH_END, u));
  const flash: N = smoothstep(0.0, 0.07, u).mul(deathFlashK);
  const hot: N = mix(vec3(1, 1, 1), col, smoothstep(0.05, 0.3, u));
  const emDis: N = hot.mul(flash.mul(mix(float(1.7), float(0.42), smoothstep(0.05, 0.3, u)))).add(mix(vec3(1, 1, 1), col, 0.35).mul(edge.mul(3.2)));

  // ---- HOLE: a round tunnel through the torso along the needle (model space)
  const isHole: N = is(2);
  const hv: N = G.sub(vec3(0, A4.y, 0));
  const along: N = hv.x.mul(sin(A3.w)).add(hv.z.mul(cos(A3.w)));
  const hd: N = max(dot(hv, hv).sub(along.mul(along)), 0).sqrt();
  const keepHole: N = float(1).sub(isHole.mul(step(hd, A4.z)));
  const rimHole: N = isHole.mul(float(1).sub(smoothstep(A4.z, A4.z.mul(1.3), hd)));

  // ---- CUT: plane at height a, tilted by b about the horizontal axis given by the angle
  const isLow: N = is(3), isHigh: N = is(4), isCut: N = isLow.add(isHigh);
  const n: N = vec3(sin(A4.z).mul(sin(A3.w)), cos(A4.z), sin(A4.z).mul(cos(A3.w)));
  const cs: N = dot(G.sub(vec3(0, A4.y, 0)), n);
  const keepCut: N = float(1).sub(isLow.mul(step(0, cs))).mul(float(1).sub(isHigh.mul(step(cs, 0))));
  const rimCut: N = isCut.mul(float(1).sub(smoothstep(0, 0.08, abs(cs))));

  const mask: N = keepDis.mul(keepHole).mul(keepCut).greaterThan(0.5);
  // back faces seen through a hole / cut: a solid cross-section glowing in the energy colour, cooling to dark
  const back: N = select(frontFacing, float(0), float(1));
  const cap: N = back.mul(isCut.add(isHole)).mul(float(0.12).add(heat.mul(1.6)));
  const emissive: N = emDis.add(col.mul(rimHole.mul(2.6).add(rimCut.mul(3)).mul(heat).add(cap)));
  const outlineColor = (ink: number): N => mix(color(ink), hot.mul(1.2), flash);
  return { mask, emissive, outlineColor };
}
